import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserAvatar } from '@/components/UserAvatar';
import { api } from '@/lib/api';
import { SHARED_EXPENSES } from '@/constants/testIds';
import { Receipt, Plus, Pencil, Trash2, Users, Loader2, ArrowRight, Wallet, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

function fmtMoney(amount, currency) {
    if (amount === null || amount === undefined || amount === '') return '—';
    try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(Number(amount));
    } catch {
        return `${currency} ${Number(amount).toLocaleString()}`;
    }
}

function ExpenseForm({ initial, currency, defaultSplit, participants, onSave, onCancel, submitting }) {
    const [description, setDescription] = useState(initial?.description ?? '');
    const [amount, setAmount] = useState(initial?.amount ?? '');
    const [splitCount, setSplitCount] = useState(initial?.split_count ?? defaultSplit ?? 1);
    const [paidBy, setPaidBy] = useState(initial?.paid_by ?? '');
    const [splitType, setSplitType] = useState(initial?.split_type === 'custom' ? 'custom' : 'equal');
    const buildEqualShareMap = useCallback((total, list) => {
        const n = Math.max(1, (list || []).length);
        const share = Math.round((Number(total || 0) / n) * 100) / 100;
        const map = {};
        let running = 0;
        (list || []).forEach((p, i) => {
            if (i === (list.length - 1)) {
                // Last participant absorbs the rounding remainder
                map[p.user_id] = Math.round((Number(total || 0) - running) * 100) / 100;
            } else {
                map[p.user_id] = share;
                running = Math.round((running + share) * 100) / 100;
            }
        });
        return map;
    }, []);
    const [customAmounts, setCustomAmounts] = useState(() => {
        if (initial?.split_type === 'custom' && initial?.custom_amounts) {
            return { ...initial.custom_amounts };
        }
        return buildEqualShareMap(initial?.amount ?? 0, participants);
    });

    const amt = Number(amount) || 0;
    const split = Math.max(1, Number(splitCount) || 1);
    const perPerson = amt / split;

    // Whenever total amount or participants change AND splitType is custom AND user hasn't customized,
    // we leave existing custom values alone. But when the user first switches to custom, recompute.
    const switchToCustom = () => {
        setSplitType('custom');
        // Always reseed with equal share when toggling so the form starts in a valid state
        setCustomAmounts(buildEqualShareMap(amt, participants));
    };

    const customTotal = Object.values(customAmounts || {}).reduce((s, v) => s + (Number(v) || 0), 0);
    const customDiff = Math.round((customTotal - amt) * 100) / 100;
    const customValid = splitType !== 'custom' || (amt > 0 && Math.abs(customDiff) <= 0.01);

    const submit = (e) => {
        e?.preventDefault?.();
        if (!description.trim()) {
            toast.error('Description is required');
            return;
        }
        if (amt <= 0) {
            toast.error('Amount must be greater than 0');
            return;
        }
        if (splitType === 'custom' && !customValid) {
            toast.error('Custom amounts must equal the expense total');
            return;
        }
        const payload = {
            description: description.trim(),
            amount: amt,
            split_count: split,
            paid_by: paidBy || null,
            split_type: splitType,
        };
        if (splitType === 'custom') {
            // Normalize to 2 decimals and only include current participants
            const ids = new Set((participants || []).map((p) => p.user_id));
            const clean = {};
            Object.entries(customAmounts || {}).forEach(([uid, v]) => {
                if (ids.has(uid)) clean[uid] = Math.round((Number(v) || 0) * 100) / 100;
            });
            payload.custom_amounts = clean;
        }
        onSave(payload);
    };

    return (
        <form
            data-testid={SHARED_EXPENSES.form}
            onSubmit={submit}
            className="rounded-2xl border border-[var(--ns-border)] p-4 sm:p-5 bg-white space-y-3"
        >
            <div>
                <label className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">Description</label>
                <Input
                    autoFocus
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="e.g. Hotel room, Car rental, Group dinner"
                    maxLength={120}
                    data-testid={SHARED_EXPENSES.descriptionInput}
                    className="rounded-xl border-[var(--ns-border)] bg-white h-11"
                />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                    <label className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">Total amount</label>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-[var(--ns-text2)] tabular-nums">{currency}</span>
                        <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="0.00"
                            data-testid={SHARED_EXPENSES.amountInput}
                            className="rounded-xl border-[var(--ns-border)] bg-white h-11"
                        />
                    </div>
                </div>
                <div>
                    <label className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">Split between</label>
                    <div className="flex items-center gap-2">
                        <Users size={14} className="text-[var(--ns-text3)]" />
                        <Input
                            type="number"
                            min="1"
                            max="999"
                            step="1"
                            value={splitCount}
                            onChange={(e) => setSplitCount(e.target.value)}
                            data-testid={SHARED_EXPENSES.splitInput}
                            disabled={splitType === 'custom'}
                            className="rounded-xl border-[var(--ns-border)] bg-white h-11 disabled:opacity-50"
                        />
                    </div>
                </div>
                <div>
                    <label className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">Per person</label>
                    <div
                        data-testid={SHARED_EXPENSES.perPersonOutput}
                        className="h-11 rounded-xl border border-dashed border-[var(--ns-border)] bg-[var(--ns-soft)] flex items-center px-3 font-display font-semibold tabular-nums text-[var(--ns-navy)]"
                    >
                        {splitType === 'custom' ? 'Custom' : fmtMoney(perPerson, currency)}
                    </div>
                </div>
            </div>

            {/* Split type toggle */}
            <div data-testid={SHARED_EXPENSES.splitTypeToggle}>
                <label className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">How to split this</label>
                <div className="inline-flex rounded-full border border-[var(--ns-border)] bg-[var(--ns-soft)] p-1">
                    <button
                        type="button"
                        onClick={() => setSplitType('equal')}
                        data-testid={SHARED_EXPENSES.splitTypeEqual}
                        className={`rounded-full h-8 px-4 text-xs font-medium transition ${splitType === 'equal' ? 'bg-[var(--ns-navy)] text-white shadow-sm' : 'text-[var(--ns-text3)] hover:text-[var(--ns-text2)]'}`}
                    >
                        Split equally
                    </button>
                    <button
                        type="button"
                        onClick={switchToCustom}
                        data-testid={SHARED_EXPENSES.splitTypeCustom}
                        className={`rounded-full h-8 px-4 text-xs font-medium transition ${splitType === 'custom' ? 'bg-[var(--ns-navy)] text-white shadow-sm' : 'text-[var(--ns-text3)] hover:text-[var(--ns-text2)]'}`}
                    >
                        Custom split
                    </button>
                </div>
            </div>

            {/* Custom amounts list */}
            {splitType === 'custom' && (
                <div data-testid={SHARED_EXPENSES.customAmountsList} className="rounded-2xl border border-[var(--ns-border)] bg-[var(--ns-soft)] p-4 space-y-2">
                    {(participants || []).map((p) => (
                        <div key={p.user_id} className="flex items-center gap-3">
                            <UserAvatar name={p.name} size={28} />
                            <span className="text-sm text-[var(--ns-text)] flex-1 truncate">{p.name}</span>
                            <span className="text-xs text-[var(--ns-text3)] tabular-nums">{currency}</span>
                            <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={customAmounts[p.user_id] ?? ''}
                                onChange={(e) => setCustomAmounts({ ...customAmounts, [p.user_id]: e.target.value })}
                                data-testid={`${SHARED_EXPENSES.customAmountInput}-${p.user_id}`}
                                className="rounded-xl border-[var(--ns-border)] bg-white h-10 w-32 tabular-nums"
                            />
                        </div>
                    ))}
                    <div className="flex items-center justify-between pt-3 border-t border-[var(--ns-border)]">
                        <span className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)]">Sum of splits</span>
                        <div className="flex items-center gap-2">
                            <span
                                data-testid={SHARED_EXPENSES.customAmountsTotal}
                                className="font-display text-base font-semibold tabular-nums"
                                style={{ color: customValid ? 'var(--ns-success,#3F8E5B)' : '#D05C45' }}
                            >
                                {fmtMoney(customTotal, currency)}
                            </span>
                            <span className="text-xs text-[var(--ns-text3)] tabular-nums">/ {fmtMoney(amt, currency)}</span>
                        </div>
                    </div>
                    {!customValid && (
                        <p
                            data-testid={SHARED_EXPENSES.customAmountsError}
                            className="text-xs text-[#D05C45] flex items-center gap-1"
                        >
                            {customDiff > 0
                                ? `Over by ${fmtMoney(Math.abs(customDiff), currency)} — reduce one or more amounts.`
                                : `Short by ${fmtMoney(Math.abs(customDiff), currency)} — add more to reach the total.`}
                        </p>
                    )}
                </div>
            )}

            <div>
                <label className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">Paid by</label>
                <Select value={paidBy || 'none'} onValueChange={(v) => setPaidBy(v === 'none' ? '' : v)}>
                    <SelectTrigger data-testid={SHARED_EXPENSES.paidBySelect} className="h-11 rounded-xl border-[var(--ns-border)] bg-white">
                        <SelectValue placeholder="Who fronted this?" />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl">
                        <SelectItem value="none">— Not assigned yet —</SelectItem>
                        {(participants || []).map((p) => (
                            <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <p className="text-xs text-[var(--ns-text3)] mt-1">Used by the Balances view below to track who owes whom.</p>
            </div>
            <div className="flex items-center gap-2 pt-1">
                <Button
                    type="submit"
                    disabled={submitting || !customValid}
                    data-testid={SHARED_EXPENSES.submitButton}
                    className="rounded-full h-10 px-5 text-white disabled:opacity-50"
                    style={{ background: 'var(--ns-navy)' }}
                >
                    {submitting ? <><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</> : (initial ? 'Save changes' : 'Add expense')}
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    onClick={onCancel}
                    data-testid={SHARED_EXPENSES.cancelButton}
                    className="rounded-full h-10 px-4"
                >
                    Cancel
                </Button>
            </div>
        </form>
    );
}

function ExpenseRow({ expense, currency, onEdit, onDelete, deleting }) {
    const isCustom = expense.split_type === 'custom';
    return (
        <div
            data-testid={SHARED_EXPENSES.item}
            data-expense-id={expense.expense_id}
            className="rounded-2xl border border-[var(--ns-border)] bg-white p-4 sm:p-5 flex items-center gap-4 group hover:border-[var(--ns-clay)] transition"
        >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--ns-soft)' }}>
                <Receipt size={16} className="text-[var(--ns-clay)]" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-[var(--ns-text)] truncate inline-flex items-center gap-2">
                    {expense.description}
                    {isCustom && (
                        <span
                            data-testid={SHARED_EXPENSES.itemSplitTypeBadge}
                            className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded-full"
                            style={{ background: 'rgba(29,45,68,0.10)', color: 'var(--ns-navy)' }}
                        >
                            Custom split
                        </span>
                    )}
                </div>
                <div className="text-xs text-[var(--ns-text3)] mt-0.5 inline-flex items-center gap-1.5 flex-wrap">
                    <span className="tabular-nums">{fmtMoney(expense.amount, currency)}</span>
                    <span>÷</span>
                    <span className="inline-flex items-center gap-0.5"><Users size={11} /> {isCustom ? 'custom' : expense.split_count}</span>
                    {expense.paid_by_name ? (
                        <span data-testid={SHARED_EXPENSES.itemPaidBy} className="inline-flex items-center gap-1 ml-1 px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(63,142,91,0.10)', color: 'var(--ns-success,#3F8E5B)' }}>
                            <Wallet size={10} /> paid by {expense.paid_by_name}
                        </span>
                    ) : (
                        <span data-testid={SHARED_EXPENSES.itemPaidBy} className="ml-1 italic" style={{ color: '#C77B3A' }}>· payer not set</span>
                    )}
                </div>
            </div>
            <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-[var(--ns-text3)]">{isCustom ? 'Split' : 'Per person'}</div>
                <div className="font-display text-lg font-semibold tabular-nums">
                    {isCustom ? 'Custom' : fmtMoney(expense.per_person, currency)}
                </div>
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                <button
                    type="button"
                    onClick={() => onEdit(expense)}
                    data-testid={SHARED_EXPENSES.editButton}
                    aria-label="Edit expense"
                    className="w-9 h-9 rounded-full hover:bg-[var(--ns-soft)] flex items-center justify-center text-[var(--ns-text2)]"
                >
                    <Pencil size={14} />
                </button>
                <button
                    type="button"
                    onClick={() => onDelete(expense)}
                    disabled={deleting}
                    data-testid={SHARED_EXPENSES.deleteButton}
                    aria-label="Delete expense"
                    className="w-9 h-9 rounded-full hover:bg-[rgba(208,92,69,0.10)] flex items-center justify-center text-[var(--ns-text2)] hover:text-[#D05C45]"
                >
                    <Trash2 size={14} />
                </button>
            </div>
        </div>
    );
}

function BalancesPanel({ settlement, currency }) {
    if (!settlement) return null;
    const { balances = [], transactions = [], is_settled } = settlement;
    return (
        <div data-testid={SHARED_EXPENSES.balancesPanel} className="mt-6 rounded-2xl border border-[var(--ns-border)] bg-[var(--ns-soft)] p-5 sm:p-6">
            <div className="flex items-center gap-3 mb-3">
                <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'white' }}>
                    <Wallet size={16} className="text-[var(--ns-navy)]" />
                </span>
                <div>
                    <div className="font-display font-semibold text-[var(--ns-text)]">Balances</div>
                    <div className="text-xs text-[var(--ns-text3)]">Who's owed what — based on the "Paid by" fields above.</div>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                {balances.map((b) => {
                    const positive = b.net > 0.01;
                    const negative = b.net < -0.01;
                    const color = positive ? 'var(--ns-success,#3F8E5B)' : negative ? '#D05C45' : 'var(--ns-text3)';
                    const label = positive ? 'is owed' : negative ? 'owes' : 'is settled';
                    return (
                        <div
                            key={b.user_id}
                            data-testid={SHARED_EXPENSES.balanceItem}
                            data-user-id={b.user_id}
                            className="bg-white rounded-xl border border-[var(--ns-border)] p-3 flex items-center gap-3"
                        >
                            <UserAvatar name={b.name} size={32} />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-[var(--ns-text)] truncate">{b.name}</div>
                                <div className="text-[11px] text-[var(--ns-text3)] tabular-nums">
                                    share {fmtMoney(b.share, currency)} · paid {fmtMoney(b.paid, currency)}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-widest" style={{ color }}>{label}</div>
                                <div className="font-display text-base font-semibold tabular-nums" style={{ color }}>
                                    {fmtMoney(Math.abs(b.net), currency)}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Settlement transactions */}
            <div className="mt-5">
                <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] mb-2">Settle up</div>
                {is_settled || transactions.length === 0 ? (
                    <div
                        data-testid={SHARED_EXPENSES.settledState}
                        className="flex items-center gap-2 text-sm text-[var(--ns-text2)] bg-white rounded-xl border border-[var(--ns-border)] p-3"
                    >
                        <CheckCircle2 size={16} className="text-[var(--ns-success,#3F8E5B)]" />
                        Everyone's even. No transactions needed.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {transactions.map((t, i) => (
                            <div
                                key={i}
                                data-testid={SHARED_EXPENSES.settlementTransaction}
                                className="bg-white rounded-xl border border-[var(--ns-border)] p-3 flex items-center gap-3 text-sm"
                            >
                                <UserAvatar name={t.from_name} size={26} />
                                <span className="font-medium text-[var(--ns-text)]">{t.from_name}</span>
                                <span className="text-[var(--ns-text3)]">owes</span>
                                <ArrowRight size={14} className="text-[var(--ns-text3)]" />
                                <UserAvatar name={t.to_name} size={26} />
                                <span className="font-medium text-[var(--ns-text)]">{t.to_name}</span>
                                <span className="ml-auto font-display font-semibold tabular-nums" style={{ color: 'var(--ns-clay)' }}>
                                    {fmtMoney(t.amount, currency)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function SharedExpenses({ trip, refreshKey }) {
    const tripId = trip.trip_id;
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/trips/${tripId}/shared-expenses`);
            setData(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not load shared expenses');
        } finally {
            setLoading(false);
        }
    }, [tripId]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const create = async (payload) => {
        setSubmitting(true);
        try {
            await api.post(`/trips/${tripId}/shared-expenses`, payload);
            toast.success('Expense added');
            setAdding(false);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not add expense');
        } finally {
            setSubmitting(false);
        }
    };

    const update = async (expenseId, payload) => {
        setSubmitting(true);
        try {
            await api.patch(`/trips/${tripId}/shared-expenses/${expenseId}`, payload);
            toast.success('Expense updated');
            setEditingId(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not update expense');
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (expense) => {
        if (!window.confirm(`Delete "${expense.description}"?`)) return;
        setDeletingId(expense.expense_id);
        try {
            await api.delete(`/trips/${tripId}/shared-expenses/${expense.expense_id}`);
            toast.success('Expense deleted');
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not delete expense');
        } finally {
            setDeletingId(null);
        }
    };

    if (loading) {
        return <div className="ns-card p-12 text-center text-[var(--ns-text2)]">Loading shared expenses…</div>;
    }

    const currency = data?.currency || trip.currency || 'USD';
    const defaultSplit = data?.default_split_count || Math.max(1, (trip.participants?.length || 1));
    const expenses = data?.expenses || [];
    const total = data?.total || 0;
    const perPersonDefault = data?.per_person_default_split || 0;
    const participants = data?.participants || (trip.participants || []).map((p) => ({ user_id: p.user_id, name: p.name }));
    const settlement = data?.settlement;

    return (
        <div data-testid={SHARED_EXPENSES.container} className="ns-card p-6 sm:p-8">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                <div>
                    <div className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(29,45,68,0.08)' }}>
                            <Receipt size={16} className="text-[var(--ns-navy)]" />
                        </span>
                        <div>
                            <div className="font-display text-lg font-semibold tracking-tight">Shared expenses</div>
                            <div className="text-xs text-[var(--ns-text3)]">Track group costs and see what each person owes.</div>
                        </div>
                    </div>
                </div>
                {!adding && !editingId && (
                    <Button
                        onClick={() => setAdding(true)}
                        data-testid={SHARED_EXPENSES.addButton}
                        className="rounded-full h-10 px-4 text-white"
                        style={{ background: 'var(--ns-clay)' }}
                    >
                        <Plus size={14} className="mr-1.5" /> Add expense
                    </Button>
                )}
            </div>

            {adding && (
                <div className="mt-4">
                    <ExpenseForm
                        currency={currency}
                        defaultSplit={defaultSplit}
                        participants={participants}
                        onSave={create}
                        onCancel={() => setAdding(false)}
                        submitting={submitting}
                    />
                </div>
            )}

            {expenses.length === 0 && !adding && (
                <div
                    data-testid={SHARED_EXPENSES.emptyState}
                    className="mt-5 rounded-2xl border border-dashed border-[var(--ns-border)] p-8 text-center"
                >
                    <Receipt size={28} className="mx-auto text-[var(--ns-text3)]" />
                    <p className="text-sm text-[var(--ns-text2)] mt-3">No shared expenses yet.</p>
                    <p className="text-xs text-[var(--ns-text3)] mt-1">Add the hotel, group dinners, car rentals — and see the per-person split.</p>
                </div>
            )}

            {expenses.length > 0 && (
                <div className="mt-5 space-y-3">
                    {expenses.map((e) => (
                        editingId === e.expense_id ? (
                            <ExpenseForm
                                key={e.expense_id}
                                initial={e}
                                currency={currency}
                                defaultSplit={defaultSplit}
                                participants={participants}
                                onSave={(payload) => update(e.expense_id, payload)}
                                onCancel={() => setEditingId(null)}
                                submitting={submitting}
                            />
                        ) : (
                            <ExpenseRow
                                key={e.expense_id}
                                expense={e}
                                currency={currency}
                                onEdit={(exp) => { setEditingId(exp.expense_id); setAdding(false); }}
                                onDelete={remove}
                                deleting={deletingId === e.expense_id}
                            />
                        )
                    ))}
                </div>
            )}

            {expenses.length > 0 && (
                <div className="mt-5 pt-5 border-t border-[var(--ns-border)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)]">Total shared costs</div>
                        <div data-testid={SHARED_EXPENSES.totalAmount} className="font-display text-2xl font-semibold tabular-nums">
                            {fmtMoney(total, currency)}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)]">
                            Per person (split {defaultSplit} ways)
                        </div>
                        <div
                            data-testid={SHARED_EXPENSES.totalPerPerson}
                            className="font-display text-2xl font-semibold tabular-nums"
                            style={{ color: 'var(--ns-clay)' }}
                        >
                            {fmtMoney(perPersonDefault, currency)}
                        </div>
                    </div>
                </div>
            )}

            {expenses.length > 0 && (
                <BalancesPanel settlement={settlement} currency={currency} />
            )}
        </div>
    );
}
